import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "./role-details.scss";
import { SearchInputComponent } from "../../../../common/components";
import { getUserDetails } from "../../../../api/lead";
import _ from "lodash";

interface RoleDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  role?: string;
  selectedCompany?: any;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: {
    id: string;
    role: string;
  };
}

interface UserData {
  data: { companyName: string; data: User[] };
}

const RoleDetails: React.FC<RoleDetailsProps> = ({
  isOpen,
  onClose,
  role,
  selectedCompany,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [userData, setUserData] = useState<UserData | null>(null);

  const fetchUsers = useCallback(
    async (search: string = "", tenantId: number) => {
      try {
        setLoading(true);
        const response = await getUserDetails({
          searchText: search,
          company: tenantId,
          isActive: true,
        }, "User Roles & Permissions");

        if (response?.data?.data) {
          setUserData(response.data.data);
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen && role && selectedCompany) {
      fetchUsers(searchText, selectedCompany);
    }
  }, [isOpen, role, selectedCompany, searchText, fetchUsers]);

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
    }, 500),
    []
  );

  const usersForRole =
    userData?.data.data
      ?.filter((user) => user.role?.role === role)
      ?.map((user) => ({
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        role: user.role?.role,
        companyName: userData?.data.companyName,
      })) || [];

  if (!isOpen) return null;

  return (
    <div className={`role-details-sidebar ${isOpen ? "open" : ""}`}>
      <div className="role-details-header">
        <h2>Role Details</h2>
        <button onClick={onClose} className="role-details-close-button">
          <CloseIcon />
        </button>
      </div>

      <div className="role-details-role-name">
        <h4>Role name</h4>
        <div>
          <input
            type="text"
            value={role}
            placeholder="Role name"
            disabled
            className="role-details-role-input"
          />
        </div>
      </div>

      <div className="role-details-body">
        <div className="role-details-body-header">
          <h4>Users Added</h4>
          <SearchInputComponent
            placeholder="Search User"
            onSearch={handleSearch}
          />
        </div>

        <div className="role-details-user-list">
          {loading ? (
            <div className="role-details-loading">Loading users...</div>
          ) : usersForRole.length > 0 ? (
            usersForRole.map((user: any, index: number) => (
              <div key={index} className="role-details-user-item">
                <div className="role-details-user-detail">
                  <div className="role-details-user-avatar">
                    <span>{user.firstName?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="role-details-user-info">
                    <div className="role-details-user-name">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="role-details-user-email">{user.email}</div>
                  </div>
                </div>
                <div className="role-details-user-company">
                  <span>{user.companyName}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="role-details-no-users">
              <p>No users found</p>
            </div>
          )}
        </div>
      </div>

      <div className="role-details-footer">
        <button onClick={onClose} className="role-details-close">
          Close
        </button>
      </div>
    </div>
  );
};

export default RoleDetails;
